import { Polygon } from 'geojson';
import { RefObject } from 'react';

import { Coordinates } from '@/types/commons/commons.types';
import { ZoneFormTypeEnum, ZoneToEditType } from '@/types/config/config.types';
import { DroneNotification, NotificationTypeEnum } from '@/types/data/data.types';
import { ReplayEvent } from '@/types/replay/replay.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';
import { AbstractFlightPlan, Activation, ActivationAndFlightPlan } from '@/types/utm/flightPlan.types';

import { SensorUniqueKeys } from './map.types';
import { AutomatonSectionUniqueKeys, ExtendedSensor, SubSensorTypeEnum, TrackLight } from './sensor/sensors';

export interface PopupPositionData {
  mapConstraintsRef: RefObject<HTMLDivElement | null>;
  top: number;
  left: number;
  onClose: () => void;
}

export enum InfoDataTypeEnum {
  INFO_AUTOMATON_SECTION = 'INFO_AUTOMATON_SECTION',
  INFO_EXPIRED_DRONE = 'INFO_EXPIRED_DRONE',
  INFO_FLIGHT_PLAN = 'INFO_FLIGHT_PLAN',
  INFO_PERIMETER_CAMERA = 'INFO_PERIMETER_CAMERA',
  INFO_SENSOR = 'INFO_SENSOR',
  INFO_POLYGON = 'INFO_POLYGON',
  INFO_STROBE = 'INFO_STROBE',
  INFO_TARGET = 'INFO_TARGET',
}

export interface InfoDataType {
  type: InfoDataTypeEnum;
}

export interface AutomatonSectionInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_AUTOMATON_SECTION;
  featureValue: AutomatonSectionUniqueKeys;
}

export interface ExpiredDroneInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_EXPIRED_DRONE;
  notification: DroneNotification;
  openingDate: Date;
}

interface BaseFlightPlanInfoData<TFlightPlan> extends InfoDataType {
  type: InfoDataTypeEnum.INFO_FLIGHT_PLAN;
  featureValue: TFlightPlan;
}

export type ActivationAndFlightPlanInfoData = BaseFlightPlanInfoData<ActivationAndFlightPlan>;

export interface FlightPlanInfoData extends BaseFlightPlanInfoData<AbstractFlightPlan> {
  activation: Activation | null;
}

export interface PerimeterCameraInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_PERIMETER_CAMERA;
  featureValue: PerimeterCamera;
}

export interface SensorInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_SENSOR;
  featureValue: SensorUniqueKeys;
}

export interface PolygonInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_POLYGON;
  targetId: string;
}

export interface StrobeInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_STROBE;
  targetId: string;
}

export interface TargetInfoData extends InfoDataType {
  type: InfoDataTypeEnum.INFO_TARGET;
  targetId: string;
  openingDate: Date;
}

export type InfoData =
  | AutomatonSectionInfoData
  | ExpiredDroneInfoData
  | FlightPlanInfoData
  | PerimeterCameraInfoData
  | SensorInfoData
  | PolygonInfoData
  | StrobeInfoData
  | TargetInfoData;

export type InfoComponentProps<TData extends InfoData> = TData & PopupPositionData;

export type EventPopupData =
  | {
      newEvent: {
        coordinates: Coordinates | null;
        date: string;
      };
      editedEvent?: never;
    }
  | {
      newEvent?: never;
      editedEvent: ReplayEvent;
    };

export type HistoryPopupData =
  | {
      identifiers: string[];
      displayId: string | null;
      sensorType?: never;
      sensorName?: never;
      sensorUniqueKeys?: never;
      type: NotificationTypeEnum.DRONE;
      openingDate: Date;
    }
  | {
      identifiers: string[];
      displayId?: never;
      sensorType: ExtendedSensor;
      sensorName: string;
      sensorUniqueKeys: SensorUniqueKeys;
      type: NotificationTypeEnum.SENSOR;
      openingDate: Date;
    }
  | {
      identifiers: string[];
      displayId?: never;
      sensorType: SubSensorTypeEnum.AUTOMATON_SEGMENT;
      sensorName: string;
      sensorUniqueKeys?: never;
      type: NotificationTypeEnum.SEGMENT;
      openingDate: Date;
    };

export type PopupDataTypeValues = HistoryPopupData['type'] | InfoDataTypeEnum;

export type ShareHoloviewPopupData = {
  site: string;
  trackIds: TrackLight[];
};

export type ConfirmationPopupData = {
  title: string;
  description: string;
  submitLabel: string;
  width?: number;
  height?: number;
  onSubmit: () => void;
};

export type RoundData = {
  featureValue: string;
};

export type GpsQrCodeData = {
  coordinates: Coordinates;
  targetId?: string;
};

export type ZoneFormData = {
  points: Polygon;
  editedZone?: ZoneToEditType;
  zoneType?: ZoneFormTypeEnum;
};

export type FlightPlanHistoryData = {
  targetId: string;
  droneCode: string;
  openingDate: Date;
};

export type DroneControlData = {
  targetId: string;
  openingDate: Date;
};

export enum PopupTypeEnum {
  EVENT = 'EVENT',
  HISTORY = 'HISTORY',
  DAILY_FLIGHT_PLAN = 'DAILY_FLIGHT_PLAN',
  FLIGHT_PLAN_HISTORY = 'FLIGHT_PLAN_HISTORY',
  INFO = 'INFO',
  REPLAY = 'REPLAY',
  RESET_SITUATION = 'RESET_SITUATION',
  CONFIRMATION_POPUP = 'CONFIRMATION_POPUP',
  VIDEO_EXPORT = 'VIDEO_EXPORT',
  SENSOR_RAW_EXPORT = 'SENSOR_RAW_EXPORT',
  ROUND = 'ROUND',
  SHARE_HOLOVIEW = 'SHARE_HOLOVIEW',
  GPS_QR_CODE = 'GPS_QR_CODE',
  MEASURE_INFO = 'MEASURE_INFO',
  FILTERS_MENU = 'FILTERS_MENU',
  DRONE_CONTROL = 'DRONE_CONTROL',
  POI = 'POI',
  ZONE = 'ZONE',
  ENGAGEMENT = 'ENGAGEMENT',
  NEUTRAL_INFO = 'NEUTRAL_INFO',
  MAP_LAYER = 'MAP_LAYER',
  STATISTIC_FILTER_MENU = 'STATISTIC_FILTER_MENU',
  ONGOING_DRONE_NOTIFICATIONS = 'ONGOING_DRONE_NOTIFICATIONS',
}

export type PopupDataType =
  | {
      type: PopupTypeEnum.EVENT;
      data: EventPopupData;
    }
  | {
      type: PopupTypeEnum.HISTORY;
      data: HistoryPopupData;
    }
  | {
      type: PopupTypeEnum.INFO;
      data: InfoData;
    }
  | {
      type: PopupTypeEnum.SHARE_HOLOVIEW;
      data: ShareHoloviewPopupData;
    }
  | {
      type:
        | PopupTypeEnum.REPLAY
        | PopupTypeEnum.RESET_SITUATION
        | PopupTypeEnum.VIDEO_EXPORT
        | PopupTypeEnum.SENSOR_RAW_EXPORT
        | PopupTypeEnum.MEASURE_INFO
        | PopupTypeEnum.FILTERS_MENU
        | PopupTypeEnum.POI
        | PopupTypeEnum.DAILY_FLIGHT_PLAN
        | PopupTypeEnum.NEUTRAL_INFO
        | PopupTypeEnum.MAP_LAYER
        | PopupTypeEnum.STATISTIC_FILTER_MENU
        | PopupTypeEnum.ONGOING_DRONE_NOTIFICATIONS;
      data: null;
    }
  | {
      type: PopupTypeEnum.CONFIRMATION_POPUP;
      data: ConfirmationPopupData;
    }
  | {
      type: PopupTypeEnum.ROUND;
      data: RoundData;
    }
  | {
      type: PopupTypeEnum.GPS_QR_CODE;
      data: GpsQrCodeData;
    }
  | {
      type: PopupTypeEnum.ZONE;
      data: ZoneFormData;
    }
  | {
      type: PopupTypeEnum.FLIGHT_PLAN_HISTORY;
      data: FlightPlanHistoryData;
    }
  | {
      type: PopupTypeEnum.DRONE_CONTROL;
      data: DroneControlData;
    }
  | {
      type: PopupTypeEnum.ENGAGEMENT;
      data: {
        openingDate: Date;
      };
    };

export type PopupType = {
  position: { x: number; y: number };
} & PopupDataType;

export type PopupComponentProps<T extends PopupTypeEnum> = PopupPositionData & {
  data: Extract<PopupDataType, { type: T }>['data'];
};
